import React, {ReactElement} from 'react';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {useTranslation} from 'react-i18next';
import {IElementStatusComponent} from '../../../../incase-domain/elements/statusComponent';
import {LIST_ITEM_MAX} from '../../../../incase-domain/validators/elements/constant';
import NumberInput from '../../../../ui/components/NumberInput';
import {removeArrayElement, swapArrayIndices} from '../../../../utils/utils';
import DebouncedTextInput from '../common/debounced/DebouncedTextInput';
import center from './center.svg';
import left from './left.svg';
import right from './right.svg';
import './status.css';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';

function HighlightedStatusComponent(): ReactElement {
    const highlightedElement = useHighlightedElement() as IElementStatusComponent;
    const {statusParts, font} = highlightedElement;
    const {t} = useTranslation();
    const updateHighlightedElement = useSetHighlightedElement();

    function removePart(partIndex: number): void {
        const newParts = removeArrayElement(statusParts, partIndex);
        updateHighlightedElement.single('statusParts', newParts);
    }

    function addPart(): void {
        const newParts = [...statusParts, {text: '', image: ''}];
        updateHighlightedElement.single('statusParts', newParts);
    }

    function swapPartHandler(newIndex: number, oldIndex: number): void {
        const newParts = swapArrayIndices(statusParts, newIndex, oldIndex);
        updateHighlightedElement.single('statusParts', newParts);
    }

    function partTextChangeHandler(partIndex: number, newValue: string): void {
        const newParts = [...statusParts];
        newParts[partIndex] = {...newParts[partIndex], text: newValue};
        updateHighlightedElement.single('statusParts', newParts);
    }

    /* function partImageChangeHandler(partIndex: number, result: string, widthHeightRatio: number): void {
        const newParts = [...statusParts];
        newParts[partIndex] = {...newParts[partIndex], image: result, imageRatio: widthHeightRatio};
        highlightedElementChangeHandler.single('statusParts', newParts);
    } */

    function statusLayoutChangeHandler(newValue: number): void {
        updateHighlightedElement.single('statusLayout', newValue);
    }

    return (
        <>
            <div className="l-group--form">
                <div className="l-group__item u-flex u-h-center u-v-center">
                    <label htmlFor="left" className="u-flex u-v-center u-h-center" title={t('designSidepanel.left')}>
                        <input
                            id="left"
                            type="radio"
                            name="left"
                            className="u-mt-0 u-mb-0"
                            checked={highlightedElement.statusLayout === 1}
                            value={1}
                            onChange={() => statusLayoutChangeHandler(1)}
                        />
                        <img className="status-layout-img" src={left} alt="" />
                    </label>

                    <label className="u-flex u-v-center u-h-center" title={t('designSidepanel.center')}>
                        <input
                            id="center"
                            type="radio"
                            name="center"
                            className="u-mt-0 u-mb-0"
                            checked={highlightedElement.statusLayout === 0}
                            value={0}
                            onChange={() => statusLayoutChangeHandler(0)}
                        />
                        <img className="status-layout-img" src={center} alt="" />
                    </label>

                    <label htmlFor="right" className="u-flex u-v-center u-h-center" title={t('designSidepanel.right')}>
                        <input
                            id="right"
                            type="radio"
                            name="right"
                            className="u-mt-0 u-mb-0"
                            checked={highlightedElement.statusLayout === 2}
                            value={2}
                            onChange={() => statusLayoutChangeHandler(2)}
                        />
                        <img className="status-layout-img" src={right} alt="" />
                    </label>
                </div>
            </div>

            <div className="l-group--form">
                <div className="l-group__item">
                    <div className="u-mb-1">{t('designSidepanel.mainImage')}</div>
                    {/* <HighlightedInputMedia
                        element="Status"
                        src={highlightedElement.statusMainImage}
                        onloadHandler={(reader, file) => {
                            if (!file) return;
                            const image = new Image();
                            const URL = window.URL || window.webkitURL;
                            image.src = URL.createObjectURL(file);
                            image.onload = () => {
                                const {clientWidth, clientHeight} = document.getElementById('slide-workspace') as HTMLElement;
                                const widthHeightRatio = (image.width / image.height) * (clientHeight / clientWidth);
                                highlightedElementChangeHandler.multiple(
                                    ['statusMainImage', 'mainImageWidthHeightRatio'],
                                    [reader.result, widthHeightRatio]
                                );
                            };
                        }}
                    /> */}
                </div>
            </div>

            <div className="l-group--form">
                <div className="l-group__item l-grid l-grid--noGaps u-flex u-v-center">
                    <div className="l-col:7">{t('designSidepanel.fontSize')}</div>
                    <div className="l-col:5">
                        <NumberInput
                            step={0.1}
                            min={0.1}
                            onChange={(newValue) =>
                                updateHighlightedElement.single('font', {
                                    ...font,
                                    size: newValue,
                                })
                            }
                            value={font.size}
                        />
                    </div>
                </div>
            </div>

            <div className="l-group--form">
                <div className="l-group__item">
                    <div className="u-flex u-h-between u-v-center u-mb-2">
                        <div>{t('designSidepanel.statusParts')}</div>
                        <button
                            aria-label="Add status part"
                            type="button"
                            onClick={() => addPart()}
                            className="p-presEdit__status_addStatusPart"
                        />
                    </div>

                    <DragDropContext
                        onDragEnd={(e) => {
                            if (typeof e.destination?.index !== 'undefined') swapPartHandler(e.destination.index, e.source.index);
                        }}
                    >
                        <Droppable droppableId={highlightedElement.id}>
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {statusParts.map((part, partIndex) => {
                                        return (
                                            <Draggable key={`${partIndex}`} draggableId={`${part.text + partIndex}`} index={partIndex}>
                                                {(provided) => {
                                                    return (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            key={`status-part-${partIndex}`}
                                                            className="u-mt-3"
                                                        >
                                                            <div className="u-mb-1">
                                                                <DebouncedTextInput
                                                                    className="e-input is-sm"
                                                                    onChange={(value) => {
                                                                        partTextChangeHandler(partIndex, value);
                                                                    }}
                                                                    value={part.text}
                                                                    maxLength={LIST_ITEM_MAX}
                                                                />
                                                            </div>
                                                            <div className="u-flex u-h-between u-v-center">
                                                                {/* <HighlightedInputMedia
                                                                    element="Status"
                                                                    src={part.image}
                                                                    onloadHandler={(reader, file) => {
                                                                        if (!file) return;

                                                                        const image = new Image();
                                                                        const URL = window.URL || window.webkitURL;
                                                                        image.src = URL.createObjectURL(file);
                                                                        image.onload = () => {
                                                                            const {clientWidth, clientHeight} = document.getElementById(
                                                                                'slide-workspace'
                                                                            ) as HTMLElement;
                                                                            const widthHeightRatio =
                                                                                (image.width / image.height) * (clientHeight / clientWidth);
                                                                            partImageChangeHandler(
                                                                                partIndex,
                                                                                reader.result as string,
                                                                                widthHeightRatio
                                                                            );
                                                                        };
                                                                    }}
                                                                /> */}
                                                                <button
                                                                    aria-label="Remove status part"
                                                                    type="button"
                                                                    onClick={() => removePart(partIndex)}
                                                                    className="p-presEdit__status_removeStatusPart"
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            </Draggable>
                                        );
                                    })}

                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </div>
        </>
    );
}

export default HighlightedStatusComponent;
